<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
// import VueTurnstile from 'vue-turnstile';
import type { FormData } from '../../types/interfaces';

// const siteKey = ref(import.meta.env.VITE_CAPTCHA_SITE_KEY);
const { tm } = useI18n();

const form = tm('formSection') as any
const backendUrl = ref(import.meta.env.VITE_BACKEND_URL);
const formData = ref<FormData>({
  name: "",
  email: "",
  message: "",
  subject: "",
  phone: "",
  file: null,
});

const isSubmitting = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const messageStatus = ref({ text: "", type: "" });
const captchaToken = ref<string | null>(null);

// const onVerify = (token: string) => {
//   captchaToken.value = token;
// };

const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    formData.value.file = target.files[0];
  }
};

// Function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Extract the base64 data without the data URL prefix
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1] as string | PromiseLike<string>;
      resolve(base64Data);
    };
    reader.onerror = error => reject(error);
  });
};

const submitForm = async () => {
  messageStatus.value = { text: "", type: "" }; // Reset message
  isSubmitting.value = true;

  // if (!captchaToken.value) {
  //   messageStatus.value = { text: "Please complete the CAPTCHA.", type: "error" };
  //   isSubmitting.value = false;
  //   return;
  // }

  // Client-side Validation
  if (!formData.value.name || !formData.value.email || !formData.value.message) {
    messageStatus.value = { text: form.validation.required, type: "error" };
    isSubmitting.value = false;
    return;
  }

  if (!formData.value.subject) {
    formData.value.subject = "General Inquiry"; // Default subject if not provided
  }

  try {
    // Prepare JSON data to send
    const jsonData: any = {
      name: formData.value.name,
      email: formData.value.email,
      subject: formData.value.subject,
      phone: formData.value.phone || "",
      message: formData.value.message,
      captcha: captchaToken.value
    };

    // Add file data if a file was selected
    if (formData.value.file) {
      const file = formData.value.file;
      jsonData.fileName = file.name;
      jsonData.fileType = file.type;
      jsonData.fileData = await fileToBase64(file);
    }

    const response = await fetch(`${backendUrl.value}`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jsonData)
    });

    // Try to parse as JSON first, fallback to text
    let responseData;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      const errorMessage = typeof responseData === 'object' && responseData.error 
        ? responseData.error 
        : (typeof responseData === 'string' ? responseData : form.messages.error);
      throw new Error(errorMessage);
    }

    // Success Handling
    formData.value = { name: "", email: "", message: "", subject: "", phone: "", file: null }; // Reset form
    if (fileInput.value) {
      fileInput.value.value = "";
    }
    
    const successMessage = typeof responseData === 'object' && responseData.message 
      ? responseData.message 
      : form.messages.success;
      
    messageStatus.value = { text: successMessage, type: "success" };
  } catch (error: Error | any) {
    messageStatus.value = { text: error.message, type: "error" };
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <section id="form">
    <form @submit.prevent="submitForm" class="space-y-4 max-w-[450px] w-full">
      <div>
        <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
          {{ form.labels.name }} <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          v-model="formData.name"
          class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          required
        />
      </div>
      <div>
        <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
          {{ form.labels.email }} <span class="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          v-model="formData.email"
          class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          required
        />
      </div>
      <div>
        <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">{{ form.labels.phoneNumber }}</label>
        <input
          type="tel"
          id="phone"
          v-model="formData.phone"
          class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
        />
      </div>
      <div>
        <label for="Subject" class="block text-sm font-medium text-gray-700 mb-1">{{ form.labels.subject }}</label>
        <select id="Subject" v-model="formData.subject" class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-transparent">
          <option>{{ form.subjectOptions.general }}</option>
          <option>{{ form.subjectOptions.project }}</option>
          <option>{{ form.subjectOptions.team }}</option>
          <option>{{ form.subjectOptions.join }}</option>
          <option>{{ form.subjectOptions.other }}</option>
        </select>
      </div>
      <div>
        <label for="message" class="block text-sm font-medium text-gray-700 mb-1">
          {{ form.labels.message }} <span class="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          v-model="formData.message"
          rows="5"
          class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          required
        ></textarea>
      </div>
      <div>
        <label for="file" class="block text-sm font-medium text-gray-700 mb-1">
          {{ form.labels.file }}
        </label>
        <input ref="fileInput" type="file" id="file" @change="handleFileUpload" accept=".pdf,.doc,.docx,.jpg,.jpeg,.zip" />
      </div>
      <!-- Button with loading animation -->
      <button
        type="submit"
        class="w-full px-6 py-3 rounded-lg transition flex items-center justify-center space-x-2 bg-indigo-600 text-white hover:bg-indigo-700"
        :disabled="isSubmitting"
      >
        <span v-if="isSubmitting" class="animate-spin border-4 border-white border-t-transparent rounded-full w-5 h-5"></span>
        <span v-if="isSubmitting">{{ form.buttons.sending }}</span>
        <span v-else>{{ form.buttons.send }}</span>
      </button>
      <!-- Status messages -->
      <p v-if="messageStatus.text" :class="messageStatus.type === 'error' ? 'text-red-500' : 'text-green-600'" class="text-sm mt-2">
        {{ messageStatus.text }}
      </p>
      <!-- <div>
        <vue-turnstile :site-key="siteKey" @verify="onVerify" v-model="captchaToken" />
      </div> -->
    </form>
  </section>
</template>